import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Receipt, Bitcoin, Gift, Banknote,
  MessageCircle, Settings as SettingsIcon, LogOut, Flame, Loader2,
  Check, X, Send, Download, ExternalLink, Image as ImageIcon,
  Wallet as WalletIcon, ShieldCheck, CreditCard,
} from "lucide-react";
import { toast } from "sonner";
import api, { ngn, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "overview", label: "Analytics", icon: LayoutDashboard },
  { id: "users", label: "Users", icon: Users },
  { id: "transactions", label: "Transactions", icon: Receipt },
  { id: "deposits", label: "Deposits", icon: WalletIcon },
  { id: "kyc", label: "KYC", icon: ShieldCheck },
  { id: "crypto", label: "Crypto Rates", icon: Bitcoin },
  { id: "giftcards", label: "Giftcard Rates", icon: Gift },
  { id: "withdrawals", label: "Withdrawals", icon: Banknote },
  { id: "payments", label: "Payment Methods", icon: CreditCard },
  { id: "chat", label: "Vendor Chat", icon: MessageCircle },
  { id: "settings", label: "Settings", icon: SettingsIcon },
];

function AdminLogin() {
  const { login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      if (u.role !== "admin") {
        logout();
        toast.error("This account is not an admin.");
      } else {
        toast.success("Welcome, admin.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tb-glow min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="w-10 h-10 rounded-xl flex items-center justify-center tb-btn-primary"><Flame size={20} /></span>
          <span className="font-head font-extrabold text-xl text-white">Travis<span className="tb-gradient-text">B</span></span>
          <span className="ml-1 text-[10px] font-bold uppercase tracking-wider bg-blue-500/20 text-blue-200 rounded px-2 py-1 border border-blue-500/30">Admin</span>
        </div>
        <div className="tb-card p-7 sm:p-8">
          <h1 className="font-head text-2xl font-bold text-white mb-1">Admin Access</h1>
          <p className="text-sm text-gray-400 mb-6">Restricted area — authorized staff only.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-400">Admin email</label>
              <input data-testid="admin-login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400" placeholder="admin@gettravisb.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-gray-400">Password</label>
              <input data-testid="admin-login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400" placeholder="••••••••" />
            </div>
            <button data-testid="admin-login-button" disabled={loading} className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-semibold tb-btn-primary disabled:opacity-60">
              {loading && <Loader2 size={18} className="animate-spin" />} Sign in to Admin
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [wa, setWa] = useState("");
  useEffect(() => { api.get("/settings").then((r) => setWa(r.data.whatsapp_number)).catch(() => {}); }, []);

  if (!ready || user === null)
    return (
      <div className="tb-glow min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-300" size={28} />
      </div>
    );
  if (!user || user.role !== "admin") return <AdminLogin />;

  return (
    <div className="tb-glow min-h-screen flex flex-col lg:flex-row">
      <aside className="lg:w-60 border-b lg:border-b-0 lg:border-r border-blue-900/30 bg-[#0A1026]/80 backdrop-blur-xl lg:fixed lg:top-0 lg:left-0 lg:bottom-0 z-40">
        <div className="p-5 flex items-center gap-2 border-b border-blue-900/30">
          <span className="w-9 h-9 rounded-xl flex items-center justify-center tb-btn-primary"><span className="font-head font-extrabold text-white">T</span></span>
          <div><div className="font-head font-extrabold text-white text-sm">TravisB</div><div className="text-[10px] uppercase tracking-wider text-blue-400">Admin</div></div>
        </div>
        <nav className="p-3 flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${tab === t.id ? "bg-blue-500/15 text-blue-200" : "text-gray-400 hover:text-white hover:bg-[#17244F]"}`}>
              <t.icon size={18} /> {t.label}
            </button>
          ))}
          <button data-testid="admin-logout-button" onClick={() => { logout(); nav("/"); }} className="flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:text-rose-300 hover:bg-rose-500/10">
            <LogOut size={18} /> Logout
          </button>
        </nav>
      </aside>

      <main className="flex-1 lg:ml-60 p-4 sm:p-8">
        <div className="max-w-6xl mx-auto">
          <h1 className="font-head text-2xl sm:text-3xl font-bold text-white mb-1">{TABS.find((t) => t.id === tab)?.label}</h1>
          <p className="text-gray-400 text-sm mb-6">Logged in as {user?.email}</p>
          {tab === "overview" && <Analytics />}
          {tab === "users" && <UsersTab />}
          {tab === "transactions" && <TransactionsTab wa={wa} />}
          {tab === "crypto" && <CryptoTab />}
          {tab === "giftcards" && <GiftcardsTab />}
          {tab === "withdrawals" && <WithdrawalsTab />}
          {tab === "deposits" && <DepositsTab />}
          {tab === "kyc" && <KycTab />}
          {tab === "payments" && <PaymentMethodsTab />}
          {tab === "chat" && <ChatTab />}
          {tab === "settings" && <SettingsTab wa={wa} setWa={setWa} />}
        </div>
      </main>
    </div>
  );
}

const SC = { pending: "text-amber-400 bg-amber-500/10", approved: "text-emerald-400 bg-emerald-500/10", completed: "text-emerald-400 bg-emerald-500/10", rejected: "text-rose-400 bg-rose-500/10" };
const Badge = ({ s }) => <span className={`text-xs font-semibold rounded-full px-2.5 py-1 capitalize ${SC[s] || "text-gray-400 bg-gray-500/10"}`}>{s}</span>;

function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <Loader2 className="animate-spin text-blue-300" />;
  const stats = [
    ["Total users", data.users], ["Transactions", data.transactions],
    ["Pending", data.pending], ["Approved", data.approved],
    ["Volume (NGN)", ngn(data.volume_ngn)], ["Pending withdrawals", data.withdrawals_pending],
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map(([l, v]) => (
          <div key={l} data-testid={`stat-${l.toLowerCase().replace(/[^a-z]/g, "-")}`} className="tb-card p-5"><div className="text-xs uppercase tracking-wider text-gray-500">{l}</div><div className="font-head text-2xl font-extrabold text-white mt-1 font-mono-tb">{v}</div></div>
        ))}
      </div>
      <div className="tb-card p-6">
        <h3 className="font-head font-semibold text-white mb-4">Recent transactions</h3>
        <div className="space-y-2">
          {data.recent.map((t) => (
            <div key={t.id} className="flex items-center justify-between p-3 rounded-xl bg-[#0A1026]/60 border border-blue-900/20">
              <div className="flex items-center gap-3"><span className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center text-blue-200">{t.type === "crypto" ? <Bitcoin size={16} /> : <Gift size={16} />}</span><div><div className="text-sm text-white font-medium">{t.user_name}</div><div className="text-xs text-gray-500">{t.type === "crypto" ? `${t.side} ${t.symbol}` : t.brand}</div></div></div>
              <div className="text-right"><div className="font-mono-tb text-sm text-emerald-400">{ngn(t.payout_ngn)}</div><Badge s={t.status} /></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  return (
    <div className="tb-card overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase tracking-wider text-gray-500 border-b border-blue-900/30"><th className="px-5 py-3">Name</th><th className="px-5 py-3">Email</th><th className="px-5 py-3">Role</th><th className="px-5 py-3">Balance</th><th className="px-5 py-3">Joined</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-blue-900/20 hover:bg-[#17244F]/40">
              <td className="px-5 py-3 text-white font-medium">{u.name}</td>
              <td className="px-5 py-3 text-gray-300">{u.email}</td>
              <td className="px-5 py-3"><span className={`text-xs rounded-full px-2 py-0.5 ${u.role === "admin" ? "bg-blue-500/20 text-blue-200" : "bg-gray-500/10 text-gray-400"}`}>{u.role}</span></td>
              <td className="px-5 py-3 font-mono-tb text-emerald-400">{ngn(u.balance_ngn)}</td>
              <td className="px-5 py-3 text-gray-500 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TransactionsTab({ wa }) {
  const [txns, setTxns] = useState([]);
  const [filter, setFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const load = () => api.get(`/admin/transactions${filter ? `?status=${filter}` : ""}`).then((r) => setTxns(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter]);

  const update = async (id, status) => {
    try { await api.patch(`/admin/transactions/${id}`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap">
        {["", "pending", "approved", "completed", "rejected"].map((f) => (
          <button key={f} data-testid={`txn-filter-${f || "all"}`} onClick={() => setFilter(f)} className={`px-4 py-2 rounded-full text-sm font-medium capitalize ${filter === f ? "tb-btn-primary" : "bg-[#101C3F] text-gray-400 border border-blue-500/20"}`}>{f || "all"}</button>
        ))}
      </div>
      <div className="space-y-3">
        {txns.map((t) => {
          const waText = encodeURIComponent(`TravisB trade: ${t.user_name} — ${t.type === "crypto" ? `${t.side} ${t.amount_crypto} ${t.symbol}` : `${t.brand} ${t.card_value}${t.currency}`} = ${ngn(t.payout_ngn)}`);
          return (
            <div key={t.id} data-testid={`admin-txn-${t.id}`} className="tb-card p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3">
                  {t.image ? (
                    <button onClick={() => setPreview(t.image)} data-testid={`txn-image-${t.id}`} className="w-16 h-16 rounded-xl overflow-hidden border border-blue-500/25 shrink-0"><img src={t.image} alt="card" className="w-full h-full object-cover" /></button>
                  ) : (
                    <span className="w-16 h-16 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-300 shrink-0">{t.type === "crypto" ? <Bitcoin size={22} /> : <Gift size={22} />}</span>
                  )}
                  <div>
                    <div className="text-sm font-semibold text-white">{t.type === "crypto" ? `${t.side?.toUpperCase()} ${t.amount_crypto} ${t.symbol}` : `${t.brand} · ${t.card_value} ${t.currency}`}</div>
                    <div className="text-xs text-gray-400">{t.user_name} · {t.user_email}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{new Date(t.created_at).toLocaleString()}</div>
                    {t.notes && <div className="text-xs text-gray-400 mt-1 italic">"{t.notes}"</div>}
                    {t.wallet_address && <div className="text-xs text-gray-400 mt-1 font-mono-tb break-all">{t.wallet_address}</div>}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono-tb font-bold text-emerald-400">{ngn(t.payout_ngn)}</div>
                  <Badge s={t.status} />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <button data-testid={`approve-txn-${t.id}`} onClick={() => update(t.id, "approved")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"><Check size={14} /> Approve</button>
                <button data-testid={`complete-txn-${t.id}`} onClick={() => update(t.id, "completed")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-500/15 text-blue-200 hover:bg-blue-500/25">Mark Paid</button>
                <button data-testid={`reject-txn-${t.id}`} onClick={() => update(t.id, "rejected")} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/15 text-rose-300 hover:bg-rose-500/25"><X size={14} /> Reject</button>
                <a href={`https://wa.me/${wa}?text=${waText}`} target="_blank" rel="noreferrer" data-testid={`whatsapp-vendor-${t.id}`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white" style={{ background: "#22C55E" }}><MessageCircle size={14} /> Send to vendor</a>
                {t.image && <a href={t.image} download={`card-${t.id}.png`} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#17244F] text-gray-300 hover:text-white"><Download size={14} /> Image</a>}
              </div>
            </div>
          );
        })}
        {txns.length === 0 && <p className="text-gray-500 text-sm py-8 text-center">No transactions.</p>}
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(10, 16, 38, 0.85)" }} onClick={() => setPreview(null)}>
          <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <img src={preview} alt="card full" className="w-full rounded-2xl border border-blue-500/30" />
            <div className="flex gap-2 mt-3 justify-center">
              <a href={preview} download="giftcard.png" className="flex items-center gap-2 px-4 py-2 rounded-full tb-btn-primary text-sm font-semibold"><Download size={16} /> Download</a>
              <button onClick={() => setPreview(null)} className="flex items-center gap-2 px-4 py-2 rounded-full border border-blue-500/40 text-white text-sm font-semibold"><X size={16} /> Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CryptoTab() {
  const [rates, setRates] = useState([]);
  const [overrides, setOverrides] = useState({});
  const load = () => {
    api.get("/crypto/rates").then((r) => setRates(r.data)).catch(() => {});
    api.get("/admin/crypto-overrides").then((r) => { const m = {}; r.data.forEach((o) => (m[o.coin] = o)); setOverrides(m); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const [draft, setDraft] = useState({});
  const save = async (coin) => {
    const d = draft[coin] || {};
    try {
      await api.post("/admin/crypto-overrides", {
        coin,
        manual_price_ngn: d.manual_price_ngn ? parseFloat(d.manual_price_ngn) : null,
        margin_percent: d.margin_percent ? parseFloat(d.margin_percent) : 0,
        active: d.active !== undefined ? d.active : true,
      });
      toast.success("Override saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-3">
      <p className="text-sm text-gray-400">Live prices come from CoinGecko. Set a manual price (overrides live) or a margin % applied on top of the live price.</p>
      {rates.map((r) => {
        const ov = overrides[r.coin] || {};
        return (
          <div key={r.coin} className="tb-card p-4" data-testid={`crypto-override-${r.symbol}`}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-full bg-blue-500/15 flex items-center justify-center font-mono-tb text-blue-200 text-xs font-bold">{r.symbol}</span>
                <div><div className="text-white font-semibold text-sm">{r.name}</div><div className="text-xs text-gray-500">Live: {ngn(r.live_ngn)} · Shown: <span className="text-emerald-400 font-mono-tb">{ngn(r.price_ngn)}</span> ({r.source})</div></div>
              </div>
              <div className="flex items-end gap-2 flex-wrap">
                <div><label className="text-[10px] uppercase text-gray-500">Manual NGN</label><input data-testid={`admin-override-rate-input-${r.symbol}`} type="number" defaultValue={ov.manual_price_ngn || ""} onChange={(e) => setDraft({ ...draft, [r.coin]: { ...draft[r.coin], manual_price_ngn: e.target.value } })} className="block w-32 px-3 py-2 rounded-lg bg-[#0A1026] border border-blue-500/25 text-white text-sm outline-none" placeholder="auto" /></div>
                <div><label className="text-[10px] uppercase text-gray-500">Margin %</label><input type="number" defaultValue={ov.margin_percent || ""} onChange={(e) => setDraft({ ...draft, [r.coin]: { ...draft[r.coin], margin_percent: e.target.value } })} className="block w-24 px-3 py-2 rounded-lg bg-[#0A1026] border border-blue-500/25 text-white text-sm outline-none" placeholder="0" /></div>
                <button data-testid={`save-override-${r.symbol}`} onClick={() => save(r.coin)} className="px-4 py-2 rounded-lg tb-btn-primary text-sm font-semibold">Save</button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function GiftcardsTab() {
  const [cards, setCards] = useState([]);
  const [form, setForm] = useState({ brand: "", category: "USD", rate_ngn: "" });
  const load = () => api.get("/admin/giftcards").then((r) => setCards(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.brand || !form.rate_ngn) return toast.error("Enter brand and rate");
    try { await api.post("/admin/giftcards", { ...form, rate_ngn: parseFloat(form.rate_ngn), active: true }); toast.success("Card added"); setForm({ brand: "", category: "USD", rate_ngn: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const update = async (c, patch) => {
    try { await api.patch(`/admin/giftcards/${c.id}`, { brand: c.brand, category: c.category, rate_ngn: c.rate_ngn, image: c.image || "", active: c.active, ...patch }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/giftcards/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Failed"); } };

  return (
    <div className="space-y-5">
      <div className="tb-card p-5 flex items-end gap-3 flex-wrap">
        <div><label className="text-[10px] uppercase text-gray-500">Brand</label><input data-testid="giftcard-brand-add-input" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="block w-44 px-3 py-2 rounded-lg bg-[#0A1026] border border-blue-500/25 text-white text-sm outline-none" placeholder="e.g. Walmart" /></div>
        <div><label className="text-[10px] uppercase text-gray-500">Currency</label><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="block w-24 px-3 py-2 rounded-lg bg-[#0A1026] border border-blue-500/25 text-white text-sm outline-none" /></div>
        <div><label className="text-[10px] uppercase text-gray-500">Rate (NGN)</label><input data-testid="giftcard-rate-add-input" type="number" value={form.rate_ngn} onChange={(e) => setForm({ ...form, rate_ngn: e.target.value })} className="block w-32 px-3 py-2 rounded-lg bg-[#0A1026] border border-blue-500/25 text-white text-sm outline-none" placeholder="1250" /></div>
        <button data-testid="giftcard-add-button" onClick={add} className="px-5 py-2 rounded-lg tb-btn-primary text-sm font-semibold">Add Card</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((c) => (
          <div key={c.id} data-testid={`admin-giftcard-${c.id}`} className="tb-card p-5">
            <div className="flex items-center justify-between mb-2"><h3 className="font-head font-semibold text-white">{c.brand}</h3><button data-testid={`delete-giftcard-${c.id}`} onClick={() => del(c.id)} className="text-rose-400 hover:text-rose-300"><X size={16} /></button></div>
            <div className="flex items-center gap-2 mb-3">
              <input type="number" defaultValue={c.rate_ngn} onBlur={(e) => update(c, { rate_ngn: parseFloat(e.target.value) })} className="w-28 px-3 py-2 rounded-lg bg-[#0A1026] border border-blue-500/25 text-emerald-400 font-mono-tb text-sm outline-none" />
              <span className="text-xs text-gray-500">/ {c.category}</span>
            </div>
            <button onClick={() => update(c, { active: !c.active })} className={`text-xs font-semibold rounded-full px-3 py-1 ${c.active ? "bg-emerald-500/15 text-emerald-300" : "bg-gray-500/15 text-gray-400"}`}>{c.active ? "Active" : "Inactive"}</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function WithdrawalsTab() {
  const [list, setList] = useState([]);
  const load = () => api.get("/admin/withdrawals").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const update = async (id, status) => { try { await api.patch(`/admin/withdrawals/${id}`, { status }); toast.success(status); load(); } catch (e) { toast.error("Failed"); } };
  return (
    <div className="space-y-3">
      {list.map((w) => (
        <div key={w.id} data-testid={`admin-withdrawal-${w.id}`} className="tb-card p-4 flex items-center justify-between flex-wrap gap-3">
          <div><div className="text-sm font-semibold text-white">{w.user_name} · {w.bank_name}</div><div className="text-xs text-gray-400 font-mono-tb">{w.account_number} — {w.account_name}</div></div>
          <div className="flex items-center gap-3"><div className="font-mono-tb font-bold text-white">{ngn(w.amount_ngn)}</div><Badge s={w.status} /><button data-testid={`approve-withdrawal-${w.id}`} onClick={() => update(w.id, "approved")} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500/15 text-emerald-300">Approve</button><button data-testid={`reject-withdrawal-${w.id}`} onClick={() => update(w.id, "rejected")} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-rose-500/15 text-rose-300">Reject</button></div>
        </div>
      ))}
      {list.length === 0 && <p className="text-gray-500 text-sm py-8 text-center">No withdrawal requests.</p>}
    </div>
  );
}

function ChatTab() {
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [image, setImage] = useState("");
  const endRef = useRef();

  const loadThreads = () => api.get("/admin/chat/threads").then((r) => setThreads(r.data)).catch(() => {});
  const loadMsgs = (uid) => api.get(`/admin/chat/${uid}`).then((r) => setMsgs(r.data)).catch(() => {});
  useEffect(() => { loadThreads(); const t = setInterval(loadThreads, 10000); return () => clearInterval(t); }, []);
  useEffect(() => { if (!active) return; loadMsgs(active.user_id); const t = setInterval(() => loadMsgs(active.user_id), 6000); return () => clearInterval(t); }, [active]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text && !image) return;
    try { await api.post(`/admin/chat/${active.user_id}`, { message: text, image }); setText(""); setImage(""); loadMsgs(active.user_id); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="tb-card grid md:grid-cols-3 h-[70vh] overflow-hidden">
      <div className="border-r border-blue-900/30 overflow-y-auto">
        {threads.map((th) => (
          <button key={th.user_id} data-testid={`chat-thread-${th.user_id}`} onClick={() => setActive(th)} className={`w-full text-left p-4 border-b border-blue-900/20 hover:bg-[#17244F] ${active?.user_id === th.user_id ? "bg-[#17244F]" : ""}`}>
            <div className="text-sm font-semibold text-white">{th.user_name}</div>
            <div className="text-xs text-gray-500 truncate">{th.last_message}</div>
          </button>
        ))}
        {threads.length === 0 && <p className="text-gray-500 text-sm p-4">No chats yet.</p>}
      </div>
      <div className="md:col-span-2 flex flex-col">
        {active ? (
          <>
            <div className="p-4 border-b border-blue-900/30 font-head font-semibold text-white">{active.user_name}</div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${m.sender === "admin" ? "tb-btn-primary" : "bg-[#17244F] text-gray-200"}`}>
                    {m.image && <img src={m.image} alt="att" className="rounded-lg mb-2 max-h-40" />}
                    {m.message && <p className="text-sm">{m.message}</p>}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-blue-900/30">
              {image && <div className="mb-2 flex items-center gap-2 text-xs text-blue-300"><ImageIcon size={14} /> attached <button onClick={() => setImage("")}><X size={14} /></button></div>}
              <div className="flex items-center gap-2">
                <label className="cursor-pointer text-gray-400 hover:text-blue-300"><ImageIcon size={20} /><input type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = () => setImage(r.result); r.readAsDataURL(f); }} /></label>
                <input data-testid="admin-chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Reply…" className="flex-1 px-4 py-2.5 rounded-full bg-[#0A1026] border border-blue-500/25 text-white outline-none text-sm" />
                <button data-testid="admin-chat-send" onClick={send} className="w-10 h-10 rounded-full flex items-center justify-center tb-btn-primary"><Send size={16} /></button>
              </div>
            </div>
          </>
        ) : <div className="flex-1 flex items-center justify-center text-gray-500 text-sm">Select a conversation</div>}
      </div>
    </div>
  );
}

function SettingsTab({ wa, setWa }) {
  const [val, setVal] = useState(wa);
  const [loading, setLoading] = useState(false);
  useEffect(() => setVal(wa), [wa]);
  const save = async () => {
    setLoading(true);
    try { const r = await api.put("/admin/settings", { whatsapp_number: val }); setWa(r.data.whatsapp_number); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setLoading(false); }
  };
  return (
    <div className="tb-card p-6 max-w-lg space-y-4">
      <div>
        <label className="text-xs uppercase tracking-wider text-gray-400">Vendor WhatsApp number (with country code, no +)</label>
        <input data-testid="settings-whatsapp-input" value={val} onChange={(e) => setVal(e.target.value)} className="mt-1.5 w-full px-4 py-3 rounded-xl bg-[#0A1026] border border-blue-500/25 text-white outline-none focus:border-blue-400 font-mono-tb" placeholder="2348012345678" />
        <p className="text-xs text-gray-500 mt-2">Used for all "Send on WhatsApp" buttons across the app.</p>
      </div>
      <button data-testid="settings-save-button" onClick={save} disabled={loading} className="flex items-center gap-2 px-6 py-3 rounded-xl tb-btn-primary font-semibold disabled:opacity-60">{loading && <Loader2 size={16} className="animate-spin" />} Save</button>
    </div>
  );
}
